import FJMediaCacheStore from "./FJMediaCacheStore";
import FJUtil from "../util/FJUtil";
import FJMedia from "./FJMedia";
import _ from 'lodash'
import FJCloudStore from "./FJCloudStore";

/**
 * @class FJMediaStore
 * @classdesc 媒体库管理器
 */
let FJMediaStore = (function () {

  let medias = [];
  let usedMediaIds =  [];

  return {

    /**
     * @description 初始化媒体资源
     * @param {Array} arr -媒体数据的数组
     * @param {Function} mediaLoadedCallback - 单个媒体加载成功后的回调
     */
    initMedias: function (arr, mediaLoadedCallback) {

      let { LOADING, LINK, DOWNLOADING } = FJMedia.MediaStatus();
      let { LOCAL, STOCK, TEMPLATE, SELF, UNSPLASH } = FJMedia.MediaFromType();
      let { DONE, UNDONE } = FJMedia.CLOUD_STATUS();
      arr.forEach(item => {
        let { id, type, fromType, name } = item;
        let media = medias.find(item => item.id === id);
        if (!media) {
          let cloudStatus = FJCloudStore.isCloudMedia(id) ? DONE : UNDONE;
          media = new FJMedia({id, type, fromType, name, status: LOADING, cloudStatus});
          medias.push(media);
        }
        // 优先检测资源是否需要 Link
        if (!FJMediaCacheStore.hasMediaCache(id)) {
					switch (fromType) {
						case LOCAL:
						    if (FJCloudStore.isCloudMedia(id)) {
                              media.setStatus(DOWNLOADING);
                              media.setProgress(0);
                              mediaLoadedCallback(media);
                            } else {
                              media.setStatus(LINK);
                              mediaLoadedCallback(media);
                            }
							break;
						case UNSPLASH:
						case SELF:
						case STOCK:
						case TEMPLATE: {
              media.setStatus(DOWNLOADING);
              media.setProgress(0);
              mediaLoadedCallback(media);
              break;
            }
					}
				}
      });

      let __chunks = _.chunk(arr, 2);
      let __length = __chunks.length;
      for (let i = 0; i < __length; i++) {
        __chunks[i].forEach(item => {
          let { id, type, fromType, name } = item;
          let { SUCCESS, LOADING, ERROR } = FJMedia.MediaStatus();
          let media = medias.find(item => item.id === id);
          if (!media) {
            media = new FJMedia({id, type, fromType, name});
            medias.push(media);
          }
          if (FJMediaCacheStore.hasMediaCache(id)) {
            let mediaCache = FJMediaCacheStore.getMediaCache(id);
            media.setStatus(LOADING);
            media.setMediaCache(mediaCache);
            mediaCache.initData().then(() => {
              return media.init(name);
            }).then(() => {
              media.setStatus(SUCCESS);
              mediaLoadedCallback(media, true);
            }).catch(() => {
              media.setStatus(ERROR);
              mediaLoadedCallback(media, true);
            });
          }

          if (media.getStatus() === DOWNLOADING) {
            mediaLoadedCallback(media, true);
          }
        });
      }
    },

    /**
     * @description 返回所有的媒体
     * @return {Array}
     */
    getMedias: function () {
      return medias;
    },

    /**
     * @description 更新媒体
     * @param {Array} list
     */
    setMedias: function (list) {
      medias = [...list];
    },

    /**
     * @description 添加媒体资源到媒体库并缓存
     * @param {string | number} id
     * @param {string} type
     * @param {string} fromType
     * @param {object} options
     * @param {object} options.name
     * @param {object} options.duration
     * @param {string} options.thumbnail
     */
    addNetMediaToLibrary: function (id, type,  fromType, options) {
      let { SUCCESS, DOWNLOADING } = FJMedia.MediaStatus();
      let {duration, thumbnail, name} = options || {};
      let media = new FJMedia({
        type,
        fromType,
        id,
	      name,
        duration,
        thumbnail,
      });
      if (FJMediaCacheStore.hasMediaCache(id)) {
        media.setStatus(SUCCESS);
        media.setMediaCache(FJMediaCacheStore.getMediaCache(id));
      } else {
        media.setStatus(DOWNLOADING);
      }
      medias.push(media);
      return media;
    },

    /**
     * @description 添加媒体资源到媒体库并缓存
     * @param {File} resource
     * @param {Function} updateMediasFunc
     */
    addLocalMediaToLibrary: async function (resource, updateMediasFunc) {

      let media = null;
      let { SUCCESS, ERROR, LOADING } = FJMedia.MediaStatus();
      let { LOCAL } = FJMedia.MediaFromType();
      let triggerUpdateFunc = () => {
        if (updateMediasFunc) {
          updateMediasFunc();
        }
      };

      try {
        let id = await FJUtil.hashEncode(resource);
        let _media = this.getMediaById(id);
        if (_media && _media.getStatus() === SUCCESS) {
          return _media;
        }

        media = medias.find(m => m.id === id);
        if (!media) {
          media = new FJMedia({
            type: FJUtil.getFileType(resource),
            fromType: LOCAL,
            status: LOADING,
            id,
            name: resource.name,
          });
          medias.unshift(media);
        }

        triggerUpdateFunc();

        if (FJMediaCacheStore.hasMediaCache(id)) {
          let mediaCache = FJMediaCacheStore.getMediaCache(id);
          await mediaCache.initData();
          media.setStatus(SUCCESS);
          media.setMediaCache(mediaCache);
        } else {
          await media.setMediaCacheWithFile(resource);
        }
        await media.init();
        media.setStatus(SUCCESS);
        triggerUpdateFunc();
      } catch (e) {
        if (media) {
          media.setStatus(ERROR);
        }
        triggerUpdateFunc();
        throw new Error(e);
      }

      return media;
    },

    /**
     * @description 对资源列表进行排序 已存在的资源放到最后
     * @param id
     */
    mediasSort: function (id) {
      let _medias = this.getMedias();
      if (_medias.length > 1 && _medias[_medias.length-1]._id !== id) {
        let y = 0;
        for (let i = 0; y < _medias.length; y++) {
          if (_medias[i]._id === id) _medias.push(_medias.splice(i,1)[0]); else i++;
        }
      }
    },

    /**
     * @description 从媒体库移除媒体并删除缓存
     * @param {string | number} id
     */
    removeMediaFromLibrary: function (id) {
      let list = [];
      medias.forEach( media => {
        if (media.id === id) {
          let { RELEASE } = FJMediaCacheStore.eventType;
          FJMediaCacheStore.dispatch(RELEASE, id);
        } else {
          list.push(media);
        }
      });
      medias = list;
    },

    /**
     * @description 从媒体库移除一组媒体并删除缓存
     * @param {Array} ids
     */
    removeMediasFromLibrary: function (ids) {
      ids.forEach(id => this.removeMediaFromLibrary(id));
    },

    /**
     * @description 更新媒体部分属性
     * @param {string | number} id
     * @param {object} options
     * @param {string} options.status
     * @param {number} options.progress
     */
    updateMedia: function (id, options) {
      let media = this.getMediaById(id);
      if (media && options) {
        if (!_.isUndefined(options.status)) {
          media.setStatus(options.status);
        }
        if (!_.isUndefined(options.progress)) {
          media.setProgress(options.progress);
        }
      }
    },

    /**
     * @description 根据 id 获取媒体
     * @param {string | number} id
     * @return {*}
     */
    getMediaById: function (id) {
      return medias.find( media => media.id === id );
    },

    /**
     * @description 是否存在某个媒体
     * @param id
     * @return {boolean}
     */
    hasMedia: function (id) {
      return !!this.getMediaById(id);
    },

    /**
     * @description 更新已使用的媒体id
     * @param id
     */
    updateUsedMediaId: function (id) {
      usedMediaIds.push(id)
    },

    /**
     * @description 媒体是否被使用过
     * @param id
     * @return {boolean}
     */
    isUsedMediaId: function (id) {
      return usedMediaIds.includes(id);
    },

    /**
     * @description 获取特定来源的媒体素材
     * @param type
     * @returns {*[]}
     */
    getMediasWithType: function (type) {
      let result = [];
      for (let i = 0; i < medias.length; i++) {
        let media = medias[i];
        if (media.fromType === type) {
          result.push(media);
        }
      }
      return result;
    },
  }
})();

export default FJMediaStore;
